import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { UploadSimple, ArrowRight, Check, Warning, MagnifyingGlass, X, FileXls, Lightning } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// ========== STEP INDICATOR ==========
const StepBar = ({ current, steps }) => (
  <div className="flex items-center gap-1 mb-6" data-testid="import-step-bar">
    {steps.map((s, i) => (
      <React.Fragment key={i}>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider ${i === current ? 'bg-blue-700 text-white' : i < current ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'}`}>
          {i < current ? <Check size={12} /> : <span>{i + 1}</span>}
          <span>{s}</span>
        </div>
        {i < steps.length - 1 && <ArrowRight size={14} className="text-zinc-300" />}
      </React.Fragment>
    ))}
  </div>
);

// ========== MAIN COMPONENT ==========
export const BOQExcelImport = ({ quoteId, onComplete }) => {
  const [step, setStep] = useState(0); // 0=upload, 1=mapping, 2=conversion, 3=confirm, 4=match, 5=done
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [targetUnit, setTargetUnit] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [matching, setMatching] = useState(false);

  const STEPS = ['Upload', 'Map Columns', 'Convert', 'Import', 'Match Master'];
  const FIELDS = [
    { key: 'description', label: 'Description', required: true },
    { key: 'unit', label: 'Unit' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'material_rate', label: 'Material Rate' },
    { key: 'labour_rate', label: 'Labour Rate' },
    { key: 'total_rate', label: 'Total Rate' },
    { key: 'specification', label: 'Specification' },
    { key: 'trade_name', label: 'Trade' },
    { key: 'make', label: 'Make/Brand' },
  ];

  const fetchTemplates = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/import-templates`, { withCredentials: true }); setTemplates(res.data); } catch {}
  }, []);

  // Step 0: Upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/api/quotes/${quoteId}/import-excel`, form, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setMapping(res.data.mapping_guess || {});
      await fetchTemplates();
      setStep(1);
      toast.success(`Parsed ${res.data.row_count} rows from ${file.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to parse Excel'); }
    setUploading(false);
  };

  const applyTemplate = (tmpl) => {
    if (tmpl.mapping) setMapping(tmpl.mapping);
    if (tmpl.target_unit) setTargetUnit(tmpl.target_unit);
    toast.success(`Applied template: ${tmpl.name}`);
  };

  // Step 3: Confirm import
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/api/quotes/${quoteId}/import-excel/confirm`, {
        preview_id: preview.preview_id,
        mapping,
        target_unit: targetUnit || undefined,
        save_template_name: templateName || undefined,
      }, { withCredentials: true });
      setImportResult(res.data);
      setStep(4);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setConfirming(false);
  };

  // Step 4: Bulk match
  const handleMatch = async () => {
    setMatching(true);
    try {
      const res = await axios.post(`${API}/api/quotes/${quoteId}/import-excel/match-master`, {}, { withCredentials: true });
      setMatchResult(res.data);
      setStep(5);
    } catch (err) { toast.error(err.response?.data?.detail || 'Match failed'); }
    setMatching(false);
  };

  // Preview data for conversion step
  const conversionPreview = preview?.sample_rows?.slice(0, 3).map(row => {
    const origUnit = String(row[mapping.unit] || '').trim();
    const origQty = parseFloat(row[mapping.quantity] || 0);
    let factor = 1, convertedUnit = origUnit;
    if (targetUnit && origUnit && targetUnit.toLowerCase() !== origUnit.toLowerCase()) {
      const key = `${origUnit.toLowerCase()}→${targetUnit.toLowerCase()}`;
      if ((preview.available_conversions || []).includes(key)) {
        const factors = { 'sqm→sft': 10.764, 'sft→sqm': 1/10.764, 'm→rft': 3.281, 'rft→m': 1/3.281 };
        factor = factors[key] || 1;
        convertedUnit = targetUnit;
      }
    }
    return { desc: String(row[mapping.description] || '').substring(0, 40), origUnit, origQty, convertedUnit, convertedQty: Math.round(origQty * factor * 100) / 100, factor };
  }) || [];

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="boq-excel-import">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileXls size={20} className="text-emerald-600" />
          <h3 className="text-base font-bold text-zinc-900">BOQ Excel Import</h3>
        </div>
        {step > 0 && step < 5 && <button onClick={() => { setStep(0); setPreview(null); }} className="text-xs text-zinc-500 hover:text-zinc-700">Start Over</button>}
      </div>

      <StepBar current={step} steps={STEPS} />

      {/* Step 0: Upload */}
      {step === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded" data-testid="upload-zone">
          <UploadSimple size={36} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-600 mb-4">Upload an Excel (.xlsx) file with BOQ line items</p>
          <label className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-none text-sm font-semibold cursor-pointer">
            <UploadSimple size={16} /> {uploading ? 'Parsing...' : 'Select File'}
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="excel-file-input" />
          </label>
        </div>
      )}

      {/* Step 1: Column Mapping */}
      {step === 1 && preview && (
        <div data-testid="mapping-step">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-600">{preview.row_count} rows detected. Map columns to BOQ fields:</p>
            {templates.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">Templates:</span>
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t)} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold border border-blue-200 px-2 py-0.5 rounded">{t.name}</button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {FIELDS.map(f => (
              <div key={f.key}>
                <label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">{f.label}{f.required && ' *'}</label>
                <select value={mapping[f.key] || ''} onChange={e => setMapping({ ...mapping, [f.key]: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2 text-xs" data-testid={`map-${f.key}`}>
                  <option value="">— Skip —</option>
                  {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          {/* Sample preview */}
          {preview.sample_rows?.length > 0 && (
            <div className="border border-zinc-200 overflow-x-auto mb-4">
              <table className="w-full text-[10px]">
                <thead><tr className="bg-zinc-50 border-b border-zinc-200">{preview.headers.slice(0, 6).map(h => <th key={h} className="p-2 text-left text-zinc-500 font-bold uppercase">{h}</th>)}</tr></thead>
                <tbody>{preview.sample_rows.slice(0, 3).map((r, i) => <tr key={i} className="border-b border-zinc-50">{preview.headers.slice(0, 6).map(h => <td key={h} className="p-2 text-zinc-700">{String(r[h] ?? '')}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}
          <Button onClick={() => { if (!mapping.description) { toast.error('Description mapping required'); return; } setStep(2); }} className="bg-blue-700 text-white rounded-none" data-testid="next-to-conversion">
            Next: Unit Conversion <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Step 2: Unit Conversion */}
      {step === 2 && (
        <div data-testid="conversion-step">
          <p className="text-sm text-zinc-600 mb-3">Optional: Convert all units to a target unit</p>
          <div className="flex items-center gap-3 mb-4">
            <div>
              <label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Target Unit</label>
              <select value={targetUnit} onChange={e => setTargetUnit(e.target.value)} className="bg-zinc-50 border border-zinc-200 rounded-none p-2 text-xs w-40" data-testid="target-unit-select">
                <option value="">No Conversion</option>
                <option value="sft">Sft (Square Feet)</option>
                <option value="rft">Rft (Running Feet)</option>
                <option value="sqm">Sqm (Square Meters)</option>
                <option value="m">M (Meters)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Save as Template</label>
              <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g. Standard BOQ" className="rounded-none text-xs w-48" data-testid="template-name-input" />
            </div>
          </div>
          {/* Conversion preview */}
          {targetUnit && conversionPreview.length > 0 && (
            <div className="border border-zinc-200 p-3 mb-4 bg-blue-50/30" data-testid="conversion-preview">
              <p className="text-[10px] font-bold text-zinc-500 uppercase mb-2">Conversion Preview</p>
              {conversionPreview.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-zinc-100 last:border-0">
                  <span className="text-zinc-800 font-medium flex-1">{c.desc}</span>
                  <span className="text-zinc-500">{c.origQty} {c.origUnit}</span>
                  <ArrowRight size={12} className="text-blue-500" />
                  <span className="text-blue-700 font-bold">{c.convertedQty} {c.convertedUnit}</span>
                  {c.factor !== 1 && <span className="text-[9px] text-zinc-400">(x{c.factor.toFixed(3)})</span>}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={() => setStep(1)} variant="outline" className="rounded-none border-zinc-300 text-xs">Back</Button>
            <Button onClick={() => setStep(3)} className="bg-blue-700 text-white rounded-none" data-testid="next-to-confirm">
              Next: Review & Import <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <div data-testid="confirm-step">
          <div className="bg-zinc-50 border border-zinc-200 p-4 mb-4 space-y-2">
            <p className="text-sm font-bold text-zinc-800">Import Summary</p>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><span className="text-zinc-500">Rows:</span> <strong>{preview?.row_count}</strong></div>
              <div><span className="text-zinc-500">Mapped:</span> <strong>{Object.keys(mapping).filter(k => mapping[k]).length} fields</strong></div>
              <div><span className="text-zinc-500">Conversion:</span> <strong>{targetUnit || 'None'}</strong></div>
              {templateName && <div><span className="text-zinc-500">Template:</span> <strong>{templateName}</strong></div>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setStep(2)} variant="outline" className="rounded-none border-zinc-300 text-xs">Back</Button>
            <Button onClick={handleConfirm} disabled={confirming} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none" data-testid="confirm-import-btn">
              {confirming ? 'Importing...' : `Import ${preview?.row_count} Items`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Match Master */}
      {step === 4 && importResult && (
        <div data-testid="match-step">
          <div className="bg-emerald-50 border border-emerald-300 p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Check size={16} className="text-emerald-600" />
              <span className="text-sm font-bold text-emerald-800">{importResult.message}</span>
            </div>
            {importResult.conversions?.length > 0 && (
              <p className="text-[10px] text-emerald-700">{importResult.conversions.length} items had unit conversion applied</p>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <MagnifyingGlass size={16} className="text-blue-600" />
              <span className="text-sm font-bold text-blue-800">Match with Master Items</span>
            </div>
            <p className="text-xs text-blue-700 mb-3">Auto-link imported items to standardized master items for better control.</p>
            <Button onClick={handleMatch} disabled={matching} className="bg-blue-700 text-white rounded-none text-xs" data-testid="run-match-btn">
              {matching ? 'Matching...' : 'Run Bulk Match'}
            </Button>
          </div>
          <button onClick={() => setStep(5)} className="text-xs text-zinc-500 hover:text-zinc-700" data-testid="skip-match">Skip matching for now</button>
        </div>
      )}

      {/* Step 5: Done / Match Results */}
      {step === 5 && (
        <div data-testid="done-step">
          {matchResult && (
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-emerald-50 border border-emerald-200 p-3 text-center"><p className="text-xl font-bold text-emerald-700">{matchResult.auto_matched}</p><p className="text-[9px] text-emerald-600 font-bold uppercase">Auto Matched</p></div>
                <div className="bg-amber-50 border border-amber-200 p-3 text-center"><p className="text-xl font-bold text-amber-700">{matchResult.review_needed}</p><p className="text-[9px] text-amber-600 font-bold uppercase">Review Needed</p></div>
                <div className="bg-zinc-50 border border-zinc-200 p-3 text-center"><p className="text-xl font-bold text-zinc-700">{matchResult.unmatched}</p><p className="text-[9px] text-zinc-500 font-bold uppercase">Unmatched</p></div>
              </div>
              {/* Results table */}
              <div className="border border-zinc-200 overflow-y-auto max-h-[300px]">
                <table className="w-full text-[10px]">
                  <thead className="sticky top-0"><tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="p-2 text-left text-zinc-500 font-bold">Item</th>
                    <th className="p-2 text-left text-zinc-500 font-bold w-24">Score</th>
                    <th className="p-2 text-left text-zinc-500 font-bold">Match</th>
                    <th className="p-2 text-center text-zinc-500 font-bold w-20">Status</th>
                  </tr></thead>
                  <tbody>{matchResult.results?.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-50">
                      <td className="p-2 text-zinc-800">{r.description?.substring(0, 50)}</td>
                      <td className="p-2"><span className={`font-bold ${r.match_score >= 90 ? 'text-emerald-700' : r.match_score >= 70 ? 'text-amber-700' : 'text-zinc-400'}`}>{r.match_score}%</span></td>
                      <td className="p-2 text-zinc-600">{r.master_item ? <span><span className="font-mono text-blue-600 text-[9px]">{r.master_item.item_code}</span> {r.master_item.description?.substring(0, 30)}</span> : '-'}</td>
                      <td className="p-2 text-center"><span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${r.status === 'auto_matched' ? 'bg-emerald-100 text-emerald-700' : r.status === 'review' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>{r.status.replace('_', ' ')}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          {/* Next Steps */}
          <div className="bg-zinc-50 border border-zinc-200 p-4" data-testid="next-steps">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={16} className="text-blue-600" />
              <span className="text-sm font-bold text-zinc-800">Next Steps</span>
            </div>
            <div className="space-y-1.5">
              {(importResult?.next_steps || ['Review imported items', 'Add recipes', 'Run demand explosion']).map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-zinc-600"><span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>{s}</div>
              ))}
            </div>
            <Button onClick={onComplete} className="mt-4 bg-blue-700 text-white rounded-none text-xs" data-testid="import-done-btn">Done</Button>
          </div>
        </div>
      )}
    </div>
  );
};
